/* MAGNAEAR / Judge.me: 6 reviews on desktop, 4 on mobile.
 * Replace the previous script; load once with defer and the matching CSS.
 */
(() => {
  'use strict';

  const INSTALL_KEY = '__magnaearJudgeMeCardsV2';
  if (window[INSTALL_KEY]) {
    window[INSTALL_KEY].refresh();
    return;
  }

  const ROOT_SELECTOR = '#judgeme_product_reviews';
  const GRID_SELECTOR = '.jdgm-masonry-grid';
  const CONTAINER_CLASS = 'jdgm-masonry-grid__container';
  const ITEM_CLASS = 'jdgm-masonry-grid__masonry-item';
  const CARD_SELECTOR = '.jdgm-review-card';
  const FOOTER_CLASS = 'jdgm-masonry-grid__footer';
  const NATIVE_BUTTON_SELECTOR = 'button.jdgm-masonry-grid__btn';
  const CONTROL_CLASS = 'jm-six-control';
  const MANAGED_CLASS = 'jm-six-managed';
  const HEIGHT_PROPERTY = '--jm-six-grid-height';
  const MOBILE_QUERY = '(max-width: 749px)';
  const FETCH_TIMEOUT = 12000;
  const states = new Map();
  const hiddenState = new WeakMap();

  function getPageSize() {
    return window.matchMedia(MOBILE_QUERY).matches ? 4 : 6;
  }

  function directChild(parent, className) {
    return Array.from(parent.children).find((node) =>
      node.classList.contains(className)
    ) || null;
  }

  function getParts(root) {
    const grid = root.querySelector(GRID_SELECTOR);
    const container = grid && directChild(grid, CONTAINER_CLASS);
    if (!container) return null;
    const items = Array.from(container.children).filter((node) =>
      node.classList.contains(ITEM_CLASS) && node.querySelector(CARD_SELECTOR)
    );
    return { grid, container, items };
  }

  function getNativeButton(grid) {
    const footer = directChild(grid, FOOTER_CLASS);
    const button = footer?.querySelector(NATIVE_BUTTON_SELECTOR);
    // The footer is hidden by our CSS while managed; do not test its computed display.
    return button && !button.hidden && button.getAttribute('aria-hidden') !== 'true'
      ? button : null;
  }

  function setHidden(item, hidden) {
    // Theme editors can clone existing DOM, including our accessibility attributes.
    if (!hiddenState.has(item) && item.dataset.jmSixHidden === 'true') {
      let previous;
      try { previous = JSON.parse(item.dataset.jmSixRestore); } catch (_) { /* Old controller. */ }
      hiddenState.set(item, previous && typeof previous.inert === 'boolean'
        ? previous : { ariaHidden: null, inert: false });
    }
    if (hidden && !hiddenState.has(item)) {
      const previous = {
        ariaHidden: item.getAttribute('aria-hidden'),
        inert: item.hasAttribute('inert')
      };
      hiddenState.set(item, previous);
      item.dataset.jmSixRestore = JSON.stringify(previous);
      item.dataset.jmSixHidden = 'true';
      item.setAttribute('aria-hidden', 'true');
      item.setAttribute('inert', '');
    } else if (!hidden && hiddenState.has(item)) {
      const previous = hiddenState.get(item);
      if (previous.ariaHidden === null) item.removeAttribute('aria-hidden');
      else item.setAttribute('aria-hidden', previous.ariaHidden);
      if (!previous.inert) item.removeAttribute('inert');
      delete item.dataset.jmSixHidden;
      delete item.dataset.jmSixRestore;
      hiddenState.delete(item);
    }
  }

  function releaseGrid(grid) {
    if (!grid) return;
    grid.querySelectorAll('[data-jm-six-hidden="true"]').forEach((item) =>
      setHidden(item, false)
    );
    if (grid.classList.contains(MANAGED_CLASS)) grid.classList.remove(MANAGED_CLASS);
    if (grid.style.getPropertyValue(HEIGHT_PROPERTY)) grid.style.removeProperty(HEIGHT_PROPERTY);
  }

  function ensureControl(root, state, grid) {
    if (!state.control || !root.contains(state.control)) {
      root.querySelectorAll(`.${CONTROL_CLASS}`).forEach((node) => node.remove());
      const control = document.createElement('div');
      control.className = CONTROL_CLASS;
      control.hidden = true;
      const button = document.createElement('button');
      button.type = 'button';
      button.className = 'jm-six-control__button';
      const label = document.createElement('span');
      label.className = 'jm-six-control__label';
      const icon = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
      icon.setAttribute('class', 'jm-six-control__icon');
      icon.setAttribute('viewBox', '0 0 24 24');
      icon.setAttribute('aria-hidden', 'true');
      icon.setAttribute('focusable', 'false');
      const path = document.createElementNS('http://www.w3.org/2000/svg', 'path');
      path.setAttribute('d', 'M6 9l6 6 6-6');
      path.setAttribute('fill', 'none');
      path.setAttribute('stroke', 'currentColor');
      path.setAttribute('stroke-width', '1.75');
      path.setAttribute('stroke-linecap', 'round');
      path.setAttribute('stroke-linejoin', 'round');
      icon.append(path);
      button.append(label, icon);
      control.append(button);
      button.addEventListener('click', () => handleClick(root, state));
      state.control = control;
      state.button = button;
      state.label = label;
    }
    if (state.control.previousElementSibling !== grid) {
      grid.insertAdjacentElement('afterend', state.control);
    }
  }

  function clearFetch(state) {
    if (state.fetchTimer) clearTimeout(state.fetchTimer);
    state.fetchTimer = 0;
    state.pendingTarget = 0;
    state.fetching = false;
    state.fetchStartCount = 0;
  }

  function armFetchTimeout(root, state) {
    if (state.fetchTimer) return;
    // Bound the entire user request, including waiting for a disabled native button.
    state.fetchTimer = window.setTimeout(() => {
      clearFetch(state);
      if (!root.isConnected) return;
      const parts = getParts(root);
      state.visibleLimit = Math.max(state.pageSize,
        Math.min(state.visibleLimit, parts?.items.length || 0));
      state.fetchFailed = true;
      scheduleSync(root, state);
    }, FETCH_TIMEOUT);
  }

  function fetchIfNeeded(root, state, parts) {
    if (!state.pendingTarget) return;
    if (parts.items.length >= state.pendingTarget) {
      state.fetchFailed = false;
      clearFetch(state);
      return;
    }
    const nativeButton = getNativeButton(parts.grid);
    if (!nativeButton) {
      clearFetch(state);
      return;
    }
    armFetchTimeout(root, state);
    if (state.fetching && parts.items.length > state.fetchStartCount) {
      state.fetching = false;
    }
    if (state.fetching || nativeButton.disabled ||
        nativeButton.getAttribute('aria-busy') === 'true') return;
    state.fetching = true;
    state.fetchStartCount = parts.items.length;
    nativeButton.click();
  }

  function updateControl(state, parts) {
    const remote = Boolean(getNativeButton(parts.grid));
    const waiting = state.pendingTarget > parts.items.length;
    const more = parts.items.length > state.visibleLimit || remote || waiting;
    state.control.hidden = !parts.grid.classList.contains(MANAGED_CLASS) ||
      !(parts.items.length > state.pageSize || remote || waiting);
    state.button.dataset.mode = more ? 'more' : 'less';
    const label = waiting ? 'טוען ביקורות…' :
      more ? (state.fetchFailed ? 'נסה שוב' : 'הראה עוד') : 'הראה פחות';
    if (state.label.textContent !== label) state.label.textContent = label;
    state.button.setAttribute('aria-expanded',
      String(Math.min(state.visibleLimit, parts.items.length) > state.pageSize));
    if (state.button.disabled !== waiting) state.button.disabled = waiting;
    if (waiting) state.button.setAttribute('aria-busy', 'true');
    else state.button.removeAttribute('aria-busy');
  }

  function scheduleMeasure(root, state) {
    if (state.measureFrame) cancelAnimationFrame(state.measureFrame);
    state.measureFrame = requestAnimationFrame(() => {
      state.measureFrame = requestAnimationFrame(() => {
        state.measureFrame = 0;
        if (!root.isConnected) return;
        const parts = getParts(root);
        if (!parts || parts.grid !== state.grid || !parts.items.length) return;
        const selected = parts.items.slice(0, state.visibleLimit);
        const gridRect = parts.grid.getBoundingClientRect();
        const rects = selected.map((item) => item.getBoundingClientRect());
        // A hidden section or unfinished layout must not lock in a stale crop.
        if (getComputedStyle(root).getPropertyValue('--jm-six-css-ready').trim() !== '1' ||
            !gridRect.width || rects.some((rect) => !rect.width || !rect.height ||
            rect.left < gridRect.left - 1 || rect.right > gridRect.right + 1)) {
          releaseGrid(parts.grid);
          state.control.hidden = true;
          return;
        }
        const bottom = Math.max(...rects.map((rect) => rect.bottom - gridRect.top));
        if (!(bottom > 0)) {
          releaseGrid(parts.grid);
          state.control.hidden = true;
          return;
        }
        // Keep the original breathing room; use the grid's own spacing when available.
        const rowGap = parseFloat(getComputedStyle(parts.container).rowGap);
        const gap = Number.isFinite(rowGap) ? rowGap : 24;
        const height = `${Math.ceil(bottom + gap)}px`;
        if (parts.grid.style.getPropertyValue(HEIGHT_PROPERTY) !== height) {
          parts.grid.style.setProperty(HEIGHT_PROPERTY, height);
        }
        parts.items.forEach((item, index) => setHidden(item, index >= state.visibleLimit));
        if (!parts.grid.classList.contains(MANAGED_CLASS)) {
          parts.grid.classList.add(MANAGED_CLASS);
        }
        updateControl(state, parts);
      });
    });
  }

  function observeLayout(state, parts) {
    if (!state.resizeObserver) return;
    const next = new Set([state.root, parts.container, ...parts.items]);
    state.observed.forEach((node) => {
      if (!next.has(node)) {
        state.resizeObserver.unobserve(node);
        state.observed.delete(node);
        setHidden(node, false);
      }
    });
    next.forEach((node) => {
      if (!state.observed.has(node)) {
        state.resizeObserver.observe(node);
        state.observed.add(node);
      }
    });
  }

  function listWasReplaced(state, items) {
    // Compare only the old prefix: appending to a short initial list is not a reset.
    return state.firstItems.length > 0 && (items.length < state.lastCount ||
      state.firstItems.some((item, index) => items[index] !== item));
  }

  function sync(root, state) {
    if (!root.isConnected) return;
    const parts = getParts(root);
    if (state.grid && state.grid !== parts?.grid) {
      releaseGrid(state.grid);
      state.grid = null;
    }
    if (!parts || !parts.items.length) {
      releaseGrid(parts?.grid);
      clearFetch(state);
      state.firstItems = [];
      state.lastCount = 0;
      state.visibleLimit = state.pageSize;
      state.fetchFailed = false;
      if (state.control) state.control.hidden = true;
      state.resizeObserver?.disconnect();
      state.observed.clear();
      return;
    }
    if (listWasReplaced(state, parts.items)) {
      state.visibleLimit = state.pageSize;
      state.fetchFailed = false;
      clearFetch(state);
    }
    if (parts.items.length > state.lastCount) state.fetchFailed = false;
    state.firstItems = parts.items.slice(0, state.pageSize);
    state.lastCount = parts.items.length;
    state.grid = parts.grid;
    ensureControl(root, state, parts.grid);
    observeLayout(state, parts);
    fetchIfNeeded(root, state, parts);
    updateControl(state, parts);
    scheduleMeasure(root, state);
  }

  function scheduleSync(root, state) {
    if (state.syncFrame || !root.isConnected) return;
    state.syncFrame = requestAnimationFrame(() => {
      state.syncFrame = 0;
      sync(root, state);
    });
  }

  function handleClick(root, state) {
    const parts = getParts(root);
    if (!parts || state.button.disabled) return;
    state.fetchFailed = false;
    if (state.button.dataset.mode === 'less') {
      clearFetch(state);
      state.visibleLimit = state.pageSize;
      sync(root, state);
      requestAnimationFrame(() => {
        if (root.isConnected) parts.grid.scrollIntoView({
          behavior: window.matchMedia('(prefers-reduced-motion: reduce)').matches
            ? 'auto' : 'smooth',
          block: 'start'
        });
      });
      return;
    }
    state.visibleLimit += state.pageSize;
    state.pendingTarget = state.visibleLimit;
    sync(root, state);
  }

  function init(root) {
    if (states.has(root)) return;
    const pageSize = getPageSize();
    const state = {
      root, pageSize, visibleLimit: pageSize, grid: null,
      pendingTarget: 0, fetching: false, fetchStartCount: 0, fetchTimer: 0,
      fetchFailed: false, syncFrame: 0, measureFrame: 0, firstItems: [],
      lastCount: 0, control: null, button: null, label: null,
      observed: new Set(), resizeObserver: null, mutationObserver: null,
      onLoad: () => scheduleMeasure(root, state)
    };
    if ('ResizeObserver' in window) {
      state.resizeObserver = new ResizeObserver(() => scheduleMeasure(root, state));
    }
    state.mutationObserver = new MutationObserver((records) => {
      const changed = records.some((record) => {
        if (record.target.closest?.(`.${CONTROL_CLASS}`)) return false;
        if (record.type === 'attributes') {
          return record.oldValue !== record.target.getAttribute(record.attributeName);
        }
        return true;
      });
      if (changed) scheduleSync(root, state);
    });
    state.mutationObserver.observe(root, {
      childList: true, subtree: true, attributes: true, attributeOldValue: true,
      attributeFilter: ['disabled', 'aria-busy', 'style', 'class', 'hidden']
    });
    root.addEventListener('load', state.onLoad, true);
    states.set(root, state);
    scheduleSync(root, state);
  }

  function cleanupDetached() {
    states.forEach((state, root) => {
      if (root.isConnected) return;
      state.mutationObserver.disconnect();
      state.resizeObserver?.disconnect();
      clearFetch(state);
      cancelAnimationFrame(state.syncFrame);
      cancelAnimationFrame(state.measureFrame);
      root.removeEventListener('load', state.onLoad, true);
      releaseGrid(state.grid);
      state.control?.remove();
      states.delete(root);
    });
  }

  function scan(scope = document) {
    if (scope.nodeType === Node.ELEMENT_NODE && scope.matches(ROOT_SELECTOR)) init(scope);
    scope.querySelectorAll?.(ROOT_SELECTOR).forEach(init);
  }

  function refresh() {
    scan();
    cleanupDetached();
    states.forEach((state, root) => {
      const pageSize = getPageSize();
      if (pageSize !== state.pageSize) {
        state.pageSize = pageSize;
        state.visibleLimit = pageSize;
        state.fetchFailed = false;
        clearFetch(state);
      }
      scheduleSync(root, state);
    });
  }

  function start() {
    scan();
    const pageObserver = new MutationObserver((records) => {
      let stylesAdded = false;
      records.forEach((record) => {
        if (record.target.matches?.('style')) stylesAdded = true;
        record.addedNodes.forEach((node) => {
        if (node.nodeType === Node.ELEMENT_NODE) {
          scan(node);
          if (node.matches('style, link[rel="stylesheet"]')) stylesAdded = true;
        }
        });
      });
      cleanupDetached();
      if (stylesAdded) refresh();
    });
    pageObserver.observe(document.documentElement, { childList: true, subtree: true });
    window.addEventListener('resize', refresh, { passive: true });
    window.addEventListener('pageshow', refresh);
    document.addEventListener('load', (event) => {
      if (event.target.matches?.('link[rel="stylesheet"]')) refresh();
    }, true);
    document.addEventListener('shopify:section:load', refresh);
    document.addEventListener('shopify:section:unload', cleanupDetached);
    document.fonts?.ready.then(refresh);
    document.fonts?.addEventListener('loadingdone', refresh);
  }

  window[INSTALL_KEY] = { refresh };
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', start, { once: true });
  } else start();
})();